import React, { FunctionComponent, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Pressable } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { InfoPopover } from '../../atoms/infoPopover/InfoPopover'
import { ConfirmationModal } from '../../organisms/confirmationModal/ConfirmationModal'
import { BodyTextSize } from '../../styles/typeStyles'
import { RowView } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { ThemeScope } from '../../utils/themes/ThemeScope'
import { tID } from '../../utils/utils'
import { ToggleSwitch } from '../toggleSwitch/ToggleSwitch'

const PrivacyGuardRow = styled(RowView)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['8px'],
  flexShrink: 0,
}))

export type PrivacyGuardControlProps = {
  privacyGuardOn: boolean
  onPrivacyGuardChange: (on: boolean) => void
}

export const PrivacyGuardControl: FunctionComponent<PrivacyGuardControlProps> = ({
  privacyGuardOn,
  onPrivacyGuardChange,
}) => {
  const intl = useIntl()
  const { colors } = useTheme() as ThemeType
  const [showConfirmToggleOffModal, setShowConfirmToggleOffModal] = useState(false)

  const handleTogglePress = (on: boolean) => {
    if (!on) {
      setShowConfirmToggleOffModal(true)
      return
    }
    onPrivacyGuardChange(true)
  }

  const handleConfirmTurnOff = () => {
    onPrivacyGuardChange(false)
    setShowConfirmToggleOffModal(false)
  }

  return (
    <PrivacyGuardRow testID={tID('ProviderJoinSessionBanner-privacyGuard')}>
      <BodyText
        text={
          <FormattedMessage
            defaultMessage='Privacy guard'
            description='Label for privacy guard toggle in provider session banner'
          />
        }
        size={BodyTextSize.DEFAULT}
      />
      <ThemeScope>
        <InfoPopover
          placement='bottom'
          shouldDisplayCloseButton={false}
          unstyledWrapper
          popoverStyle={{ container: { width: '220px' } }}
          trigger={
            <Pressable
              accessibilityRole='button'
              accessibilityLabel={intl.formatMessage({
                defaultMessage: 'Privacy guard information',
                description:
                  'Accessibility label for the privacy guard info icon button in the provider session banner',
              })}
            >
              <InfoIcon
                size={18}
                fillColor={colors.iconDefault}
                testID={tID('ProviderJoinSessionBanner-privacyGuardInfo')}
              />
            </Pressable>
          }
          content={
            <BodyText
              size={BodyTextSize.SMALL}
              text={
                <FormattedMessage
                  defaultMessage='Use privacy guard when screen sharing to disable parts of the Lyra platform to prevent PHI leaks'
                  description='Tooltip explaining the privacy guard toggle in the provider session banner'
                />
              }
            />
          }
        />
      </ThemeScope>
      <ToggleSwitch
        testID={tID('ProviderJoinSessionBanner-privacyGuardToggle')}
        isOn={privacyGuardOn}
        onPress={handleTogglePress}
        offColor={colors.iconLightBackground}
        onColor={colors.iconActive}
        handleColor={colors.iconDarkBackground}
        accessibilityLabel={intl.formatMessage({
          defaultMessage: 'Privacy guard',
          description: 'Accessibility label for privacy guard toggle in provider session banner',
        })}
      />
      <ThemeScope forceLightMode>
        <ConfirmationModal
          testID={tID('ProviderJoinSessionBanner-privacyGuardConfirmToggleOffModal')}
          visible={showConfirmToggleOffModal}
          modalTitle={intl.formatMessage({
            defaultMessage: 'Turn off privacy guard?',
            description: 'Title of modal confirming the provider wants to turn off privacy guard',
          })}
          modalContents={intl.formatMessage({
            defaultMessage: 'Double check you are not sharing your screen before turning privacy guard off.',
            description: 'Warning shown before the provider turns off privacy guard',
          })}
          confirmationButtonText={intl.formatMessage({
            defaultMessage: 'Turn off',
            description: 'Button confirming the provider wants to turn off privacy guard',
          })}
          cancelButtonText={intl.formatMessage({
            defaultMessage: 'Keep on',
            description: 'Button to keep privacy guard on and dismiss the turn off confirmation modal',
          })}
          onConfirmationButtonPress={handleConfirmTurnOff}
          onRequestClose={() => setShowConfirmToggleOffModal(false)}
          singleLineButtons
        />
      </ThemeScope>
    </PrivacyGuardRow>
  )
}
